import Tippy from '@tippyjs/react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useContext, useMemo, useState, VFC } from 'react';

import { useMyUserFavorite } from '@/apis/aniftyBackend/creations/userFavorite/useMyUserFavorite';
import { isCreator, User } from '@/apis/aniftyBackend/users/interface';
import CustomButton from '@/components/CustomButton';
import EditButton from '@/components/CustomButton/EditButton';
import FloatingModal from '@/components/FloatingModal';
import LinkToAuthorizedRoute from '@/components/LinkToAuthorizedRoute';
import ShareSNSButton from '@/components/ShareSNSButton/ShareSNSButton';
import SwapWithIPFS from '@/components/SwapWithIPFS';
import { UserFavoriteButton } from '@/components/UserFavoriteButton';
import { IS_LISTING_FEATURE_END } from '@/config/closure';
import redirectTargets from '@/config/redirectTargets';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import EditableProfileImage from '@/containers/UserProfilePage/EditableProfileImage';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useTryToAuthorize from '@/hooks/useTryToAuthorize';
import CertifiedBadge from '@/icons/certified-badge.svg';
import NetworkIcon from '@/icons/network.svg';
import createMiddleEllipsis from '@/utils/createMiddleEllipsis';
import generateSquareImageURL from '@/utils/generateSquareImageURL';
import getDisplayName from '@/utils/getDisplayName';

import styles from './UserProfilePage.module.scss';

type Props = {
  user: User;
};

const ProfileInfoSection: VFC<Props> = ({ user: _user }: Props) => {
  const [user, setUser] = useState(_user);
  const { t, i18n } = useTranslation();

  const web3Context = useContext(Web3Context);

  const [hasCopied, setHasCopied] = useState(false);

  const profilePhotoURL = useMemo(() => {
    return user.profilePicUrl
      ? generateSquareImageURL(user.profilePicUrl, 150)
      : DEFAULT_PROFILE_IMAGE_PATH;
  }, [user]);

  // update profile picture url to reflect changes instantly
  const handleProfilePhotoUpdate = (profilePhotoURL: string) => {
    setUser({
      ...user,
      profilePicUrl: profilePhotoURL,
    });
  };

  const copyPublicAddress = async () => {
    await navigator.clipboard.writeText(user.userID);
    setHasCopied(true);
  };

  const isMyProfile =
    web3Context.currentAccountAddress &&
    web3Context.currentAccountAddress.toLowerCase() === user.userID;

  const isProfileReady = !!user.creatorProfile || !!user.collectorProfile;

  // prepare display name
  const displayName = getDisplayName(user, i18n.language);

  const [isCertifiedModalOpen, setIsCertifiedModalOpen] = useState(false);

  const { isUserFavorited, toggle: toggleUserFavorite } = useMyUserFavorite({
    targetUserID: user.userID,
  });

  const tryToAuthorize = useTryToAuthorize();

  const handleUserFavoriteButtonClick = async () => {
    const isAuthorized = await tryToAuthorize({
      redirectTarget: user.userName
        ? redirectTargets.creatorDetail(user.userName)
        : redirectTargets.userDetail(user.userID),
    });

    if (!isAuthorized) {
      return;
    }

    await toggleUserFavorite();
  };

  return (
    <div className={styles.profileContainer}>
      {isMyProfile ? (
        <EditableProfileImage
          currentProfileImageURL={profilePhotoURL}
          onUpdated={handleProfilePhotoUpdate}
        />
      ) : (
        <div className={styles.profileImage}>
          <img src={profilePhotoURL} alt="profile picture" />
        </div>
      )}
      <div className={styles.topRightButtonGroup}>
        {isMyProfile ? (
          <>
            {isCreator(user) && !IS_LISTING_FEATURE_END ? (
              <Link href={routes.createCreation}>
                <CustomButton>{t('header.create')}</CustomButton>
              </Link>
            ) : null}
            <SwapWithIPFS />
            <LinkToAuthorizedRoute href={routes.accountSettings} passHref>
              <EditButton />
            </LinkToAuthorizedRoute>
            <ShareSNSButton userTwitter={user.twitterID} />
          </>
        ) : (
          <>
            <UserFavoriteButton
              isFavorited={isUserFavorited}
              onClick={handleUserFavoriteButtonClick}
            />
            <ShareSNSButton
              userTwitter={user.twitterID}
              placeCenterWhenMobile
            />
          </>
        )}
      </div>

      <div className={styles.userProfile}>
        {isProfileReady ? (
          /* User Profile is ready */
          <>
            <h1 className={styles.userDisplayName}>
              {displayName}
              {isCreator(user) && (
                <>
                  <Tippy content={t('userProfilePage.certifiedArtist')}>
                    <button
                      type="button"
                      onClick={() => setIsCertifiedModalOpen(true)}
                      className={styles.certifiedBadgeButton}
                    >
                      <CertifiedBadge />
                    </button>
                  </Tippy>
                  <FloatingModal
                    isOpen={isCertifiedModalOpen}
                    onRequestClose={() => setIsCertifiedModalOpen(false)}
                  >
                    <div className={styles.certifiedArtistInfo}>
                      <CertifiedBadge />
                      <p>
                        {t('userProfilePage.certifiedArtistModal.description')}
                      </p>
                      <CustomButton
                        variant="primary"
                        onClick={() => setIsCertifiedModalOpen(false)}
                      >
                        {t('userProfilePage.certifiedArtistModal.buttonText')}
                      </CustomButton>
                    </div>
                  </FloatingModal>
                </>
              )}
            </h1>
            <Tippy
              content={hasCopied ? 'Copied!' : 'Copy Address'}
              placement="bottom"
              hideOnClick={false}
              onHide={() => setHasCopied(false)}
            >
              <button
                type="button"
                className={styles.userIdCopyButton}
                onClick={() => copyPublicAddress()}
              >
                <span className={styles.userPublicAddress}>{user.userID}</span>
                <img src="/images/user-profile-page/copy-icon.svg" alt="copy" />
              </button>
            </Tippy>
            {user.about && <p className={styles.userAbout}>{user.about}</p>}
            {user.twitterID && (
              <a
                href={`https://twitter.com/${user.twitterID}`}
                target="_blank"
                rel="noreferrer"
                className={styles.userTwitterLink}
              >
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 1.89411C19.2651 2.21538 18.4739 2.43265 17.6439 2.52972C18.4915 2.03048 19.1419 1.23886 19.4483 0.295847C18.6547 0.758108 17.7778 1.0944 16.841 1.27468C16.0944 0.489996 15.0273 0 13.8463 0C11.5819 0 9.74467 1.8086 9.74467 4.039C9.74467 4.35565 9.77989 4.66305 9.85033 4.9589C6.4401 4.79018 3.41727 3.18382 1.39226 0.737306C1.03892 1.33593 0.837002 2.03048 0.837002 2.7701C0.837002 4.17075 1.56131 5.4073 2.66244 6.13189C1.99096 6.11224 1.35705 5.92849 0.802958 5.62802V5.67772C0.802958 7.63539 2.21753 9.26833 4.09579 9.63814C3.75183 9.7329 3.38909 9.78028 3.01462 9.78028C2.75048 9.78028 2.49222 9.75601 2.24218 9.70863C2.76457 11.3127 4.27892 12.481 6.07501 12.5122C4.67101 13.5962 2.90075 14.2423 0.979045 14.2423C0.648001 14.2423 0.320479 14.2238 0 14.1868C1.81605 15.3309 3.9737 16 6.28984 16C13.8381 16 17.9644 9.84615 17.9644 4.5082C17.9644 4.33254 17.9609 4.15688 17.9539 3.98469C18.7556 3.41495 19.4518 2.70423 20 1.89411Z"
                    fill="rgba(29,161,242,1.00)"
                  />
                </svg>
                {user.twitterID}
              </a>
            )}
            {user.portfolioLinks.filter(Boolean).map((link, index) => {
              let fullLink = link;
              if (!/^https?:\/\//.test(link)) {
                fullLink = '//' + link;
              }
              return (
                <div className={styles.userPortfolioLinkWrapper}>
                  <NetworkIcon className={styles.networkIcon} />
                  <a
                    href={fullLink}
                    key={`portfolio_link_${index}`}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.userPortfolioLink}
                  >
                    {link}
                  </a>
                </div>
              );
            })}
          </>
        ) : (
          /* Profile is not set yet */
          <Tippy
            content={hasCopied ? 'Copied!' : 'Copy Address'}
            placement="bottom"
            hideOnClick={false}
            onHide={() => setHasCopied(false)}
          >
            <button
              type="button"
              className={styles.userIdCopyButton}
              onClick={() => copyPublicAddress()}
            >
              <h1 className={styles.userIdText}>
                {createMiddleEllipsis(user.userID)}
              </h1>
              <img src="/images/user-profile-page/copy-icon.svg" alt="copy" />
            </button>
          </Tippy>
        )}
      </div>
    </div>
  );
};

export default ProfileInfoSection;
